import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Lock, Mail, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin login (email + password) — separate from the Landing's Google CTA.
 * Reachable at /admin-login. Anyone with admin credentials can use it; regular
 * users continue using "Accedi con Google" from the Landing.
 *
 * Flow:
 *  1. Type email → onBlur asks backend `/auth/has-password` to know whether
 *     a password input should be revealed (Google-only accounts won't have one).
 *  2. Submit calls `/auth/login-password`. Cookie is set by backend, AuthContext
 *     refreshes via `/auth/me` and routes to /admin/vip if is_admin else /dashboard.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [hasPwd, setHasPwd] = useState(null);   // null → unknown; true/false → checked
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      navigate(user.is_admin ? "/admin/vip" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const checkHasPassword = async () => {
    const e = email.trim().toLowerCase();
    if (!e) { setHasPwd(null); return; }
    setChecking(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/has-password`, { email: e },
        { withCredentials: true },
      );
      setHasPwd(Boolean(data?.has_password));
    } catch {
      setHasPwd(null);
    } finally {
      setChecking(false);
    }
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/login-password`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true },
      );
      await refresh();
      // Navigation handled by useEffect once user is populated.
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login fallito");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
         style={{ background: "linear-gradient(135deg, #0F0820 0%, #1B1235 60%, #0F0820 100%)" }}>
      <div className="absolute top-6 left-6">
        <Link to="/" data-testid="admin-login-back"
              className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
        </Link>
      </div>

      <div className="w-full max-w-md rounded-3xl p-8 shadow-2xl"
           style={{ background: "rgba(20, 14, 40, 0.85)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(176, 156, 255, 0.18)" }}
           data-testid="admin-login-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #B09CFF, #7c5cff)" }}>
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">Accesso riservato</p>
            <h1 className="text-xl font-serif text-white">Admin · Academia VIP</h1>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/70 mb-1.5 block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="admin-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setHasPwd(null); }}
                onBlur={checkHasPassword}
                className="w-full rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-[#B09CFF]/50"
                style={{ background: "rgba(15, 8, 32, 0.7)",
                         border: "1px solid rgba(176, 156, 255, 0.25)" }}
                placeholder="email@esempio.com"
              />
            </div>
            {hasPwd === false && email && (
              <p className="text-[11px] mt-1.5 text-amber-300/90" data-testid="admin-login-google-hint">
                Questo account utilizza Google. Usa "Accedi con Google" dalla home.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-white/70 mb-1.5 block">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-[#B09CFF]/50"
                style={{ background: "rgba(15, 8, 32, 0.7)",
                         border: "1px solid rgba(176, 156, 255, 0.25)" }}
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-300 px-3 py-2 rounded-lg"
               style={{ background: "rgba(239, 68, 68, 0.12)" }}
               data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={submitting || !email || !password}
            className="w-full rounded-lg py-2.5 text-sm font-medium text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all hover:brightness-110"
            style={{ background: "linear-gradient(135deg, #B09CFF, #7c5cff)" }}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {submitting ? "Verifica…" : "Accedi"}
          </button>

          <p className="text-[10px] text-center text-white/40 pt-2">
            Per gli utenti regolari, usa{" "}
            <Link to="/" className="text-[#B09CFF] hover:underline">Accedi con Google</Link>{" "}
            dalla home.
          </p>
        </form>
      </div>
    </div>
  );
}
